/**
 * Surveys — athlete-facing fill screen for custom forms (the periodic / monthly
 * "deep" survey, distinct from the quick Daily Check-In). Lists the gym's active
 * forms; the athlete fills one and submits. Coaches see results in Form Reports.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';

export default function Surveys() {
  const toast = useToast();
  const [athleteId, setAthleteId] = useState(null);
  const [forms, setForms] = useState([]);
  const [active, setActive] = useState(null);   // form being filled
  const [values, setValues] = useState({});      // field_id -> value
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`${API_BASE_URL}/api/athlete/me`)
      .then(r => { if (alive) setAthleteId(r.data?.id ?? null); })
      .catch(() => { if (alive) setAthleteId(null); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/v1/wellness/custom-forms`);
      setForms(Array.isArray(data) ? data : []);
    } catch { setForms([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (f) => { setActive(f); setValues({}); };
  const setVal = (fid, v) => setValues(s => ({ ...s, [fid]: v }));

  const submit = async () => {
    if (!athleteId) { toast.error('Could not find your athlete profile.'); return; }
    const missing = (active.fields || []).filter(f => f.is_required && (values[f.id] == null || values[f.id] === ''));
    if (missing.length) { toast.error(`Please answer: ${missing.map(m => m.field_label || m.field_name).join(', ')}`); return; }
    setSubmitting(true);
    try {
      const field_responses = (active.fields || []).map(f => {
        const v = values[f.id];
        const numeric = (f.field_type === 'number' || f.field_type === 'scale') && v !== '' && v != null ? Number(v) : null;
        return { field_id: f.id, response_value: v == null ? null : String(v), response_numeric: numeric };
      });
      await api.post(`${API_BASE_URL}/api/v1/wellness/forms/${active.id}/responses`, { athlete_id: athleteId, field_responses });
      toast.success('Survey submitted — thank you!');
      setActive(null); setValues({});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not submit the survey.');
    } finally { setSubmitting(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '.9rem' };

  const renderField = (f) => {
    const v = values[f.id] ?? '';
    const opts = Array.isArray(f.options) ? f.options : (() => { try { return JSON.parse(f.options || '[]'); } catch { return []; } })();
    switch (f.field_type) {
      case 'scale': {
        const min = f.min_value != null ? Number(f.min_value) : 1;
        const max = f.max_value != null ? Number(f.max_value) : 5;
        const nums = []; for (let n = min; n <= max; n++) nums.push(n);
        return (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {nums.map(n => (
              <button key={n} type="button" onClick={() => setVal(f.id, n)}
                style={{ minWidth: 40, padding: '8px 0', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
                  border: Number(v) === n ? '1px solid #4f46e5' : '1px solid #d1d5db',
                  background: Number(v) === n ? '#4f46e5' : '#fff', color: Number(v) === n ? '#fff' : '#475569' }}>{n}</button>
            ))}
          </div>
        );
      }
      case 'number': return <input type="number" value={v} onChange={e => setVal(f.id, e.target.value)} style={{ ...inputStyle, width: 140 }} placeholder={f.placeholder_text || ''} />;
      case 'dropdown': return (
        <select value={v} onChange={e => setVal(f.id, e.target.value)} style={inputStyle}>
          <option value="">Select…</option>
          {opts.map((o, i) => <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : (o.label || o.value)}</option>)}
        </select>
      );
      case 'checkbox': {
        const arr = Array.isArray(v) ? v : (v ? String(v).split(',') : []);
        const toggle = (o) => setVal(f.id, arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {opts.map((o, i) => { const val = typeof o === 'string' ? o : o.value; return (
              <label key={i} style={{ fontSize: '.9rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={arr.includes(val)} onChange={() => toggle(val)} /> {typeof o === 'string' ? o : (o.label || o.value)}
              </label>
            ); })}
          </div>
        );
      }
      case 'date': return <input type="date" value={v} onChange={e => setVal(f.id, e.target.value)} style={{ ...inputStyle, width: 200 }} />;
      case 'time': return <input type="time" value={v} onChange={e => setVal(f.id, e.target.value)} style={{ ...inputStyle, width: 160 }} />;
      default: return <textarea value={v} onChange={e => setVal(f.id, e.target.value)} rows={2} style={inputStyle} placeholder={f.placeholder_text || ''} />;
    }
  };

  return (
    <div style={{ padding: '1rem', maxWidth: 620, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#111827', margin: 0 }}>📋 Surveys</h1>
      <p style={{ fontSize: '.9rem', color: '#6b7280', margin: '4px 0 18px' }}>
        Periodic check-ins from your coaches. (Your quick daily readiness is the <strong>Daily Check-In</strong>.)
      </p>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p>
        : active ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '1.1rem 1.25rem' }}>
            <button onClick={() => setActive(null)} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '.85rem', padding: 0 }}>← All surveys</button>
            <h2 style={{ fontSize: '1.1rem', margin: '8px 0 4px' }}>{active.form_name || active.name}</h2>
            {active.description && <p style={{ color: '#6b7280', fontSize: '.88rem', marginTop: 0 }}>{active.description}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
              {(active.fields || []).map(f => (
                <div key={f.id}>
                  <div style={{ fontSize: '.9rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                    {f.field_label || f.field_name}{f.is_required && <span style={{ color: '#dc2626' }}> *</span>}
                    {f.unit && <span style={{ color: '#9ca3af', fontWeight: 400 }}> ({f.unit})</span>}
                  </div>
                  {renderField(f)}
                  {f.help_text && <div style={{ fontSize: '.78rem', color: '#9ca3af', marginTop: 3 }}>{f.help_text}</div>}
                </div>
              ))}
            </div>
            <button onClick={submit} disabled={submitting}
              style={{ marginTop: 18, padding: '10px 20px', borderRadius: 8, background: '#4f46e5', color: '#fff', fontWeight: 700, border: 'none', cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
              {submitting ? 'Submitting…' : 'Submit survey'}
            </button>
          </div>
        ) : forms.length === 0 ? (
          <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12, padding: '1.25rem', color: '#64748b', fontSize: '.9rem' }}>
            No surveys right now. Your coaches will post them here when there's one to fill.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {forms.map(f => (
              <button key={f.id} onClick={() => openForm(f)}
                style={{ textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '.9rem 1.1rem', cursor: 'pointer' }}>
                <div style={{ fontWeight: 700, color: '#111827' }}>{f.form_name || f.name}</div>
                <div style={{ fontSize: '.83rem', color: '#6b7280' }}>{f.field_count ?? (f.fields || []).length} question{(f.field_count ?? (f.fields || []).length) === 1 ? '' : 's'}{f.description ? ` · ${f.description}` : ''}</div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}
